import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../config/runtime';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  TextField,
  Card,
  CardContent,
  CardActions,
  Stack,
  Switch,
  Tooltip,
  RadioGroup,
  Radio,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  ContactPhone as ContactPhoneIcon,
} from '@mui/icons-material';

interface AppUser {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  isActive: boolean;
  emailConfirmed: boolean;
  mfaEnabled: boolean;
  contactId: string | null;
  contactName: string | null;
}

interface ContactOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

interface UserDeleteBusinessImpact {
  businessId: string;
  businessName: string;
  willBeFullyDeleted: boolean;
  deviceCount: number;
  contactCount: number;
  hasActiveSubscription: boolean;
  reassignedPrimaryUserName: string | null;
}

interface UserDeletePreview {
  userId: string;
  email: string;
  fullName: string;
  businesses: UserDeleteBusinessImpact[];
}

interface AvailableRole {
  id: string;
  name: string;
  description?: string;
}

const UserManagement: React.FC = () => {
  const auth = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState<AppUser[]>([]);
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create User Dialog
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    fullName: '',
    password: '',
    roleNames: [] as string[],
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // Assign Role Dialog
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [assigningRole, setAssigningRole] = useState(false);

  // Change Password Dialog
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Link Contact Dialog
  const [contactDialogUser, setContactDialogUser] = useState<AppUser | null>(null);
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactOption | null>(null);
  const [savingContact, setSavingContact] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  // Delete User Dialog
  const [deleteTarget, setDeleteTarget] = useState<AppUser | null>(null);
  const [deleteMode, setDeleteMode] = useState<'soft' | 'hard'>('soft');
  const [deletePreview, setDeletePreview] = useState<UserDeletePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const apiUrl = getApiBaseUrl();

  useEffect(() => {
    if (auth.isSignedIn) {
      fetchUsers();
      fetchAvailableRoles();
    }
  }, [auth.isSignedIn]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/api/auth/users`, {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/auth/roles`, {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableRoles(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to fetch roles', err);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.fullName || !newUser.password) {
      setError('Email, full name, and password are required');
      return;
    }

    try {
      setCreatingUser(true);
      const response = await fetch(`${apiUrl}/api/auth/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({
          email: newUser.email,
          fullName: newUser.fullName,
          password: newUser.password,
          roleNames: newUser.roleNames,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user');
      }

      setOpenCreateDialog(false);
      setNewUser({
        email: '',
        fullName: '',
        password: '',
        roleNames: [],
      });
      await fetchUsers();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUserId || !selectedRoleId) return;

    try {
      setAssigningRole(true);
      const response = await fetch(`${apiUrl}/api/auth/users/${selectedUserId}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({ roleId: selectedRoleId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign role');
      }

      setOpenRoleDialog(false);
      await fetchUsers();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setAssigningRole(false);
    }
  };

  const handleRemoveRole = async (userId: string, roleName: string) => {
    if (!window.confirm(`Remove ${roleName} from this user?`)) return;

    try {
      const response = await fetch(`${apiUrl}/api/auth/users/${userId}/roles/${roleName}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to remove role');
        } catch (parseError) {
          throw new Error('Failed to remove role');
        }
      }

      await fetchUsers();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleToggleMfa = async (userId: string, enabled: boolean) => {
    try {
      const response = await fetch(`${apiUrl}/api/auth/users/${userId}/mfa`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({ enabled }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update MFA');
      }

      await fetchUsers();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const openContactDialog = async (user: AppUser) => {
    setContactDialogUser(user);
    setContactError(null);
    setSelectedContact(user.contactId ? { id: user.contactId, firstName: user.contactName ?? '', lastName: '', email: '' } : null);

    if (contacts.length === 0) {
      setLoadingContacts(true);
      try {
        const response = await fetch(`${apiUrl}/api/contacts`, {
          headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
        });
        if (response.ok) {
          setContacts(await response.json());
        }
      } finally {
        setLoadingContacts(false);
      }
    }
  };

  const handleSaveContact = async () => {
    if (!contactDialogUser) return;
    setSavingContact(true);
    setContactError(null);
    try {
      const response = await fetch(`${apiUrl}/api/auth/users/${contactDialogUser.id}/contact`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({ contactId: selectedContact?.id ?? null }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update linked contact');
      }

      setContactDialogUser(null);
      await fetchUsers();
      setError(null);
    } catch (err) {
      setContactError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSavingContact(false);
    }
  };

  const openDeleteDialog = (user: AppUser) => {
    setDeleteTarget(user);
    setDeleteMode('soft');
    setDeletePreview(null);
    setAdminPassword('');
    setDeleteError(null);
  };

  const handleDeleteModeChange = async (mode: 'soft' | 'hard') => {
    setDeleteMode(mode);
    setDeleteError(null);
    if (mode === 'hard' && deleteTarget && !deletePreview) {
      setLoadingPreview(true);
      try {
        const response = await fetch(`${apiUrl}/api/auth/users/${deleteTarget.id}/delete-preview`, {
          headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to load delete preview');
        }
        setDeletePreview(await response.json());
      } catch (err) {
        setDeleteError(err instanceof Error ? err.message : 'Failed to load delete preview');
      } finally {
        setLoadingPreview(false);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const response = await fetch(`${apiUrl}/api/auth/users/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({
          hardDelete: deleteMode === 'hard',
          adminPassword: deleteMode === 'hard' ? adminPassword : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete user');
      }

      setDeleteTarget(null);
      await fetchUsers();
      setError(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    try {
      setChangingPassword(true);
      const response = await fetch(`${apiUrl}/api/auth/users/${passwordUserId}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({ newPassword }),
      });
      if (!response.ok) {
        let errorMessage = 'Failed to change password';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // response body was not JSON (e.g. empty 401/403)
        }
        throw new Error(errorMessage);
      }
      setOpenPasswordDialog(false);
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleExpandUser = (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          mb: 3,
        }}
      >
        <Typography variant="h6">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setNewUser({ email: '', fullName: '', password: '', roleNames: [] });
            setOpenCreateDialog(true);
          }}
        >
          Create User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {users.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No users found</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {users.map((user) => (
            <Card key={user.id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>{user.fullName}</Typography>
                  <Chip
                    label={user.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={user.isActive ? 'success' : 'default'}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Contact: {user.contactName || 'None linked'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2">SMS MFA on login</Typography>
                  <Switch
                    size="small"
                    checked={user.mfaEnabled}
                    onChange={(e) => handleToggleMfa(user.id, e.target.checked)}
                  />
                </Box>

                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 0.5 }} color="text.secondary">
                  <SecurityIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Roles ({user.roles.length})
                </Typography>
                {user.roles.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No roles assigned</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {user.roles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        size="small"
                        onDelete={() => handleRemoveRole(user.id, role)}
                        deleteIcon={<DeleteIcon />}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
              <CardActions sx={{ flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedUserId(user.id);
                    setSelectedRoleId('');
                    setOpenRoleDialog(true);
                  }}
                >
                  Add Role
                </Button>
                <Button
                  size="small"
                  startIcon={<LockIcon />}
                  onClick={() => {
                    setPasswordUserId(user.id);
                    setNewPassword('');
                    setConfirmPassword('');
                    setOpenPasswordDialog(true);
                  }}
                >
                  Change Password
                </Button>
                <Button
                  size="small"
                  startIcon={<ContactPhoneIcon />}
                  onClick={() => openContactDialog(user)}
                >
                  Link Contact
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => openDeleteDialog(user)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>
                  <Tooltip title="SMS verification code required at login">
                    <span>MFA</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <React.Fragment key={user.id}>
                  <TableRow>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleExpandUser(user.id)}
                      >
                        {expandedUsers.has(user.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.fullName}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={user.contactName ? 'text.primary' : 'text.secondary'}>
                        {user.contactName || 'None linked'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={user.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={user.mfaEnabled}
                        onChange={(e) => handleToggleMfa(user.id, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setSelectedRoleId('');
                          setOpenRoleDialog(true);
                        }}
                        sx={{ mr: 1 }}
                      >
                        Add Role
                      </Button>
                      <Button
                        size="small"
                        startIcon={<LockIcon />}
                        onClick={() => {
                          setPasswordUserId(user.id);
                          setNewPassword('');
                          setConfirmPassword('');
                          setOpenPasswordDialog(true);
                        }}
                      >
                        Change Password
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => openContactDialog(user)}
                        title="Link contact"
                      >
                        <ContactPhoneIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(user)}
                        title="Delete user"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                      <Collapse in={expandedUsers.has(user.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <SecurityIcon sx={{ mr: 1, fontSize: 18 }} />
                            Roles ({user.roles.length})
                          </Typography>
                          {user.roles.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No roles assigned
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {user.roles.map((role) => (
                                <Chip
                                  key={role}
                                  label={role}
                                  onDelete={() => handleRemoveRole(user.id, role)}
                                  deleteIcon={<DeleteIcon />}
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create User Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              placeholder="user@example.com"
            />
            <TextField
              label="Full Name"
              fullWidth
              value={newUser.fullName}
              onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
              placeholder="John Doe"
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              placeholder="Enter temporary password"
            />
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={newUser.roleNames}
                onChange={(e) =>
                  setNewUser({
                    ...newUser,
                    roleNames: typeof e.target.value === 'string' ? [e.target.value] : e.target.value,
                  })
                }
                label="Roles"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.id} value={role.name}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={!newUser.email || !newUser.fullName || !newUser.password}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Role Dialog */}
      <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Role to User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Role</InputLabel>
              <Select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                label="Select Role"
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRoleDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAssignRole}
            variant="contained"
            disabled={!selectedRoleId}
          >
            Assign Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change User Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="New Password"
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 8 characters"
            />
            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              error={confirmPassword.length > 0 && newPassword !== confirmPassword}
              helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>Cancel</Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={!newPassword || !confirmPassword || changingPassword}
          >
            {changingPassword ? 'Saving...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Contact Dialog */}
      <Dialog open={!!contactDialogUser} onClose={() => !savingContact && setContactDialogUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Contact — {contactDialogUser?.fullName}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {contactError && <Alert severity="error" sx={{ mb: 2 }}>{contactError}</Alert>}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              At most one login can be linked to a given contact card. Choose a contact,
              or clear the field to unlink.
            </Typography>
            <Autocomplete
              options={contacts}
              loading={loadingContacts}
              value={selectedContact}
              onChange={(_, value) => setSelectedContact(value)}
              getOptionLabel={(c) => `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ''}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Contact" placeholder="Search contacts…" />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogUser(null)} disabled={savingContact}>Cancel</Button>
          <Button onClick={handleSaveContact} variant="contained" disabled={savingContact}>
            {savingContact ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User — {deleteTarget?.fullName}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}

            <RadioGroup
              value={deleteMode}
              onChange={(e) => handleDeleteModeChange(e.target.value as 'soft' | 'hard')}
            >
              <FormControlLabel
                value="soft"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Soft delete (recommended)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Deactivates the account. Nothing else is touched — fully reversible.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="hard"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="error">Hard delete</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Permanently deletes the user. Cannot be undone.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>

            {deleteMode === 'hard' && (
              <>
                {loadingPreview ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : deletePreview && deletePreview.businesses.length === 0 ? (
                  <Alert severity="info">This user has no business memberships — only their account will be deleted.</Alert>
                ) : deletePreview ? (
                  <Box>
                    <Typography variant="body2" fontWeight={600} gutterBottom>This will affect:</Typography>
                    <List dense disablePadding>
                      {deletePreview.businesses.map((b) => (
                        <ListItem key={b.businessId} disableGutters sx={{ display: 'block' }}>
                          {b.willBeFullyDeleted ? (
                            <ListItemText
                              primary={<Typography variant="body2" color="error">{b.businessName} — will be permanently deleted</Typography>}
                              secondary={`${b.deviceCount} device(s), ${b.contactCount} contact(s)${b.hasActiveSubscription ? ', active subscription' : ''}`}
                            />
                          ) : (
                            <ListItemText
                              primary={<Typography variant="body2">{b.businessName} — user will just be removed</Typography>}
                              secondary={b.reassignedPrimaryUserName ? `Primary ownership reassigned to ${b.reassignedPrimaryUserName}` : undefined}
                            />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : null}

                <TextField
                  fullWidth
                  type="password"
                  label="Your admin password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  helperText="Confirm it's you before permanently deleting this user"
                  disabled={deleting}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color={deleteMode === 'hard' ? 'error' : 'primary'}
            variant="contained"
            disabled={deleting || loadingPreview || (deleteMode === 'hard' && !adminPassword)}
          >
            {deleting ? 'Deleting…' : deleteMode === 'hard' ? 'Permanently Delete' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
